import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Text from '../text/text';
import ImageWithPlaceholder from '../image/image-with-placeholder';

export interface AdditionalFeaturesProps {
  /** The text displayed at the top of the section */
  heading?: string;
  /** Heading variant for the section title */
  headingVariant?: Lucid.Theme.Component.Typography.Heading.VariantKeys;
  /** The text displayed beneath the heading */
  subheading?: string;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Content for the features to be displayed */
  features: Lucid.Directus.Generated.Additional_Features[];
}

const AdditionalFeatures: FC<PropsWithChildren<AdditionalFeaturesProps>> = ({
  className,
  heading,
  headingVariant = 'heading2XL',
  subheading,
  features,
}) => (
  <Box
    as="section"
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Heading as="h2" css={theme => ({ margin: 0, paddingBottom: theme.space.s24 })} variant={headingVariant}>
      {heading}
    </Heading>
    <Text css={theme => ({ margin: 0, paddingBottom: theme.space.s80, maxWidth: '580px' })} variant="paragraphM300">
      {subheading}
    </Text>
    <Box
      css={theme => ({
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        columnGap: theme.space.s120,
        rowGap: theme.space.s80,
      })}
    >
      {features.map(feature => (
        <Box key={feature.heading} css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ImageWithPlaceholder
            alt=""
            aspectRatio="1:1"
            css={theme => ({ paddingBottom: '180px', width: '180px', marginBottom: theme.space.s24 })}
            src={withImage(feature.icon)}
          />
          <Heading
            as="h3"
            css={theme => ({ textAlign: 'center', [theme.breakpoints.bp1024.min]: { maxWidth: '180px' } })}
            variant="headingM"
          >
            {feature.heading}
          </Heading>
        </Box>
      ))}
    </Box>
  </Box>
);

export { AdditionalFeatures };
export default AdditionalFeatures;
